import React, {
  useState,
} from 'react';
import isNil from 'lodash/isNil';
import {
  useNotify,
} from 'core/hooks';
import {
  useGetPatientTrend,
  useMarkPatientReviewed,
} from 'pages/Dashboard/pages/Dashboard/RTM/hooks';
import PatientTrendCharts, {
  PainMode,
} from 'pages/Dashboard/pages/Dashboard/RTM/Trends/PatientTrendCharts';
import PatientTrendSummary from 'pages/Dashboard/pages/Dashboard/RTM/Trends/PatientTrendSummary';
import PatientTrendsDialogSkeleton from 'pages/Dashboard/pages/Dashboard/RTM/Trends/PatientTrendsDialogSkeleton';
import {
  getTrendChartRows,
} from 'pages/Dashboard/pages/Dashboard/RTM/Trends/helpers';
import {
  rtmEventChannel,
} from 'pages/Dashboard/services/eventChannels';

type Props = {
  patientId: number;
};

export default function PatientTrendsContent({
  patientId,
}: Props) {
  const notify = useNotify();
  const {
    data: patientTrend,
    isFetching,
    refetch: refetchPatientTrend,
  } = useGetPatientTrend(patientId);
  const {
    mutateAsync: markPatientReviewed,
    isPending: isMarkReviewedPending,
  } = useMarkPatientReviewed();
  const [painMode, setPainMode] = useState<PainMode>('composite');
  const chartRows = getTrendChartRows(patientTrend?.points);
  const isInitialLoading = isFetching && isNil(patientTrend);
  const hasNoCheckIn = !isInitialLoading && isNil(patientTrend?.lastCheckIn);
  const hasCheckIn = !isInitialLoading && !hasNoCheckIn;

  async function handleMarkReviewed() {
    try {
      await markPatientReviewed({ patientId });
      rtmEventChannel.emit('onRTMPatientDataChange', patientId);
      await refetchPatientTrend();
      notify({
        variant: 'success',
        message: 'Patient marked as reviewed',
      });
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <div className="rtm-trends-content min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 md:p-5">
      {isInitialLoading ? (
        <PatientTrendsDialogSkeleton />
      ) : null}
      {hasNoCheckIn ? (
        <div className="text-sm text-gray-500 -m-4 -mt-5 bg-gray-50 p-4">
          No check in yet
        </div>
      ) : null}
      {hasCheckIn ? (
        <div className="flex flex-col gap-3.5">
          <PatientTrendSummary
            patientTrend={patientTrend}
            chartRows={chartRows}
            isMarkReviewedPending={isMarkReviewedPending}
            onMarkReviewed={handleMarkReviewed}
            reviewedByUser={patientTrend?.lastReviewedBy ?? undefined}
          />
          <div className="flex flex-col gap-1">
            <div className="text-lg font-semibold text-gray-800">
              Trends
            </div>
            <PatientTrendCharts
              chartRows={chartRows}
              patientTrend={patientTrend}
              painMode={painMode}
              onPainModeChange={setPainMode}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
